import React, { useState, useRef, useEffect } from 'react';
import { Add as Plus, Check, Close as X, DisplaySettings, Save, CloudUpload } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FilterSection from '../../components/ScreenCreation/FilterSection';
import TabBar from '../../components/ScreenCreation/TabBar';
import ContainerCard from '../../components/ScreenCreation/ContainerCard';
import { schemaService } from '../../services/schemaService';
import { getRequest, postRequest } from '../../globalhelper/helper';
import { Box, TextField, IconButton, Button, Typography, Autocomplete, Tabs, Tab, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    height: '100%',
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

export const ScreenMapping = () => {
  // Load workspace tabs dynamically using the Schema Service API (each section represents a Tab)
  const [tabs, setTabs] = useState(() => schemaService.getWorkspaceTabs());

  // Set default active tab dynamically per subCategory
  const [activeTabIds, setActiveTabIds] = useState(() => {
    const initialTabs = schemaService.getWorkspaceTabs();
    const ids = {};
    initialTabs.forEach(t => {
      const subCat = t.subCategory || t.category || 'Other';
      if (!ids[subCat]) ids[subCat] = t.id;
    });
    return ids;
  });

  // Load container configurations dynamically for each tab using the Schema Service API
  // In standard tabs, each container card represents a specific metadata Field
  const [tabContainers, setTabContainers] = useState(() => {
    const initialTabs = schemaService.getWorkspaceTabs();
    const initialMap = {};
    initialTabs.forEach(t => {
      initialMap[t.id] = schemaService.getContainers(t.id);
    });
    return initialMap;
  });

  const [addingContainerCategory, setAddingContainerCategory] = useState(null);
  const [newContainerTitle, setNewContainerTitle] = useState('');
  const containerInputRef = useRef(null);

  // Added state to check if the user has selected filters
  const [hasSearched, setHasSearched] = useState(false);
  const [currentTenantId, setCurrentTenantId] = useState('');
  const [currentScreenName, setCurrentScreenName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, title: '', message: '' });

  const handleSearch = (searchData) => {
    if (!searchData) {
      setHasSearched(false);
      setCurrentTenantId('');
      setCurrentScreenName('');
      return;
    }

    const { tenantId, screenName } = searchData;
    setCurrentTenantId(tenantId);
    setCurrentScreenName(screenName);

    getRequest(`api/edureka/getScreenMappingData?tenantId=${tenantId}&screenName=${screenName}`)
      .then(res => {
        let data = res?.res?.data || res?.data || res;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch (e) { }
        }

        // Update schemaService with fetched data
        schemaService.setData(data);

        // Re-initialize state based on new schema data
        const newTabs = schemaService.getWorkspaceTabs();
        setTabs(newTabs);

        const newActiveIds = {};
        const newMap = {};
        newTabs.forEach(t => {
          const subCat = t.subCategory || t.category || 'Other';
          if (!newActiveIds[subCat]) newActiveIds[subCat] = t.id;
          newMap[t.id] = schemaService.getContainers(t.id);
        });

        setActiveTabIds(newActiveIds);
        setTabContainers(newMap);
        setHasSearched(true);
      })
      .catch(err => {
        console.error("Failed to load screen mapping data:", err);
        setErrorMsg("Failed to load screen mapping data.");
        setHasSearched(false);
      });
  };

  useEffect(() => {
    if (addingContainerCategory && containerInputRef.current) {
      containerInputRef.current.focus();
    }
  }, [addingContainerCategory]);

  const handleAddTab = (name, category = 'Other', subCategory = null) => {
    const newTabId = name.trim();
    const resolvedSubCategory = subCategory || category;

    // Automatically prefix the new tab ID if adding to a numbered subCategory
    let finalTabId = newTabId;
    if (resolvedSubCategory !== category && !newTabId.toLowerCase().startsWith(resolvedSubCategory.toLowerCase())) {
      finalTabId = `${resolvedSubCategory}-${newTabId}`;
    }

    const newTab = { id: finalTabId, name: newTabId, category, subCategory: resolvedSubCategory };
    setTabs([...tabs, newTab]);
    setTabContainers(prev => ({
      ...prev,
      [finalTabId]: []
    }));
    setActiveTabIds(prev => ({ ...prev, [resolvedSubCategory]: finalTabId }));
  };

  const handleRenameTab = (tabId, newName) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, name: newName, id: newName } : t));
    setTabContainers(prev => {
      const dataCopy = { ...prev };
      if (dataCopy[tabId]) {
        dataCopy[newName] = dataCopy[tabId];
        delete dataCopy[tabId];
      }
      return dataCopy;
    });
    // Update activeTabIds if the renamed tab was active
    setActiveTabIds(prev => {
      const updated = { ...prev };
      for (const cat in updated) {
        if (updated[cat] === tabId) {
          updated[cat] = newName;
        }
      }
      return updated;
    });
  };

  const handleToggleCollapse = (tabId, containerId) => {
    setTabContainers(prev => {
      const activeContainers = prev[tabId] || [];
      const updated = activeContainers.map(c =>
        c.id === containerId ? { ...c, isCollapsed: !c.isCollapsed } : c
      );
      return {
        ...prev,
        [tabId]: updated
      };
    });
  };

  // Add sub-properties inline inside the field's data model keys list
  const handleAddSubProperty = (tabId, containerId, name, type, value) => {
    setTabContainers(prev => {
      const activeContainers = prev[tabId] || [];
      const updated = activeContainers.map(c => {
        if (c.id === containerId) {
          const updatedData = { ...c.data };
          let finalVal = value;
          if (type === 'Checkbox' || type === 'Boolean') {
            finalVal = value.toLowerCase() === 'true';
          } else if (type === 'Number') {
            finalVal = Number(value);
          } else if (type === 'Object') {
            finalVal = {};
          } else if (type === 'Array') {
            finalVal = [];
          }
          updatedData[name] = finalVal;
          return { ...c, data: updatedData };
        }
        return c;
      });
      return {
        ...prev,
        [tabId]: updated
      };
    });
  };

  // Generic callback handling edits occurring at any node level in the JSON outline tree
  const handleUpdateContainerData = (tabId, containerId, updatedData) => {
    setTabContainers(prev => {
      const activeContainers = prev[tabId] || [];
      const updated = activeContainers.map(c => {
        if (c.id === containerId) {
          // Synchronize card title dynamically if FieldName (standard tab) or Type (schema tab) is renamed inside tree
          const newTitle = tabId === 'schema-tab'
            ? (updatedData.Type || c.title)
            : (updatedData.FieldName || c.title);

          return {
            ...c,
            data: updatedData,
            title: newTitle
          };
        }
        return c;
      });
      return {
        ...prev,
        [tabId]: updated
      };
    });
  };

  // Renaming headers directly: syncs bidirectional keys back to FieldName or Type
  const handleRenameContainer = (tabId, containerId, newTitle) => {
    setTabContainers(prev => {
      const activeContainers = prev[tabId] || [];
      const updated = activeContainers.map(c => {
        if (c.id === containerId) {
          const updatedData = { ...c.data };
          if (tabId === 'schema-tab') {
            updatedData.Type = newTitle;
          } else {
            updatedData.FieldName = newTitle;
          }
          return {
            ...c,
            title: newTitle,
            data: updatedData
          };
        }
        return c;
      });
      return {
        ...prev,
        [tabId]: updated
      };
    });
  };

  const handleStartAddContainer = (category) => {
    setAddingContainerCategory(category);
    setNewContainerTitle('');
  };

  // Saves a new collapsible container (a Field object in standard tabs, or a config object in Schema tab)
  const handleSaveContainer = () => {
    const trimmedTitle = newContainerTitle.trim();
    if (trimmedTitle && addingContainerCategory) {
      const tabId = activeTabIds[addingContainerCategory];
      const isSchema = tabId === 'schema-tab';
      const containerId = Date.now().toString();

      const newContainer = {
        id: containerId,
        title: trimmedTitle,
        isCollapsed: false, // Expands automatically
        data: isSchema ? {
          Type: trimmedTitle,
          ScreenName: 'Bill Extraction',
          moduleName: 'DTFOWS'
        } : {
          FieldName: trimmedTitle,
          FieldType: 'input',
          FieldEditable: 'true',
          FieldID: Date.now()
        }
      };

      setTabContainers(prev => {
        const activeContainers = prev[tabId] || [];
        return {
          ...prev,
          [tabId]: [...activeContainers, newContainer]
        };
      });
      setAddingContainerCategory(null);
    } else {
      handleCancelContainer();
    }
  };

  const handleCancelContainer = () => {
    setAddingContainerCategory(null);
    setNewContainerTitle('');
  };

  const handleContainerKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveContainer();
    } else if (e.key === 'Escape') {
      handleCancelContainer();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event, tabId) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTabContainers((prev) => {
        const activeContainers = Array.from(prev[tabId] || []);

        const oldIndex = activeContainers.findIndex(c => c.id === active.id);
        const newIndex = activeContainers.findIndex(c => c.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(activeContainers, oldIndex, newIndex);

          const updatedContainers = reordered.map((container, index) => ({
            ...container,
            data: {
              ...container.data,
              FieldID: index + 1
            }
          }));

          return {
            ...prev,
            [tabId]: updatedContainers
          };
        }
        return prev;
      });
    }
  };

  const handleUpdateTab = (tabId, updatedTab) => {
    setTabs(prev => prev.map(t => t.id === tabId ? updatedTab : t));
  };



  const categories = ['Header', 'Body', 'Footer', 'Other'];
  const groupedTabs = tabs.reduce((acc, tab) => {
    const cat = tab.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tab);
    return acc;
  }, {});

  const moveOptions = [];
  categories.forEach(cat => {
    if (cat === 'Header') {
      moveOptions.push({ category: 'Header', subCategory: 'Header', label: 'Header' });
      return;
    }
    if (cat === 'Footer') {
      moveOptions.push({ category: 'Footer', subCategory: 'Footer', label: 'Footer' });
      return;
    }

    const catTabs = groupedTabs[cat] || [];
    const subCats = new Set(catTabs.map(t => t.subCategory || t.category || 'Other'));
    subCats.forEach(sc => {
      moveOptions.push({ category: cat, subCategory: sc, label: sc });
    });

    if (cat === 'Body') {
      let maxNum = 0;
      subCats.forEach(sc => {
        const match = sc.match(/^(\d+)-/);
        if (match) {
          maxNum = Math.max(maxNum, parseInt(match[1]));
        }
      });

      const nextNum = Math.max(maxNum, 0) + 1;
      const nextSubCat = `${nextNum}-${cat.toLowerCase()}`;
      if (!subCats.has(nextSubCat)) {
        moveOptions.push({ category: cat, subCategory: nextSubCat, label: `${nextSubCat} (New)` });
      }
    }
  });

  const handleMoveTab = (tabId, destCategory, destSubCategory) => {
    let newTabId = tabId;

    setTabs(prev => {
      const tabsInDest = prev.filter(t => (t.id || t.ScreenName) !== tabId && t.subCategory === destSubCategory);

      let maxNum = 0;
      tabsInDest.forEach(t => {
        const sec = t.Section || '';
        const match = sec.match(/-(\d+)$/);
        if (match) {
          maxNum = Math.max(maxNum, parseInt(match[1]));
        }
      });
      const nextSequenceNumber = maxNum + 1;

      let newSection = '';
      if (destCategory.toLowerCase() === 'footer') {
        newSection = `footer-${nextSequenceNumber}`;
      } else if (destCategory.toLowerCase() === 'body') {
        newSection = `${destSubCategory}-${nextSequenceNumber}`;
      } else {
        newSection = `${destSubCategory}-${nextSequenceNumber}`;
      }

      newTabId = newSection;

      return prev.map(t => {
        if ((t.id || t.ScreenName) === tabId) {
          return {
            ...t,
            category: destCategory,
            subCategory: destSubCategory,
            Section: newSection,
            id: newSection
          };
        }
        return t;
      });
    });

    setActiveTabIds(prev => ({
      ...prev,
      [destSubCategory]: newTabId
    }));

    setTabContainers(prev => {
      if (newTabId === tabId) return prev;
      const newContainers = { ...prev };
      if (newContainers[tabId]) {
        const updatedItems = newContainers[tabId].map(container => {
          if (container.data && typeof container.data === 'object') {
            return { ...container, data: { ...container.data, Section: newTabId } };
          }
          return container;
        });
        newContainers[newTabId] = updatedItems;
        delete newContainers[tabId];
      }
      return newContainers;
    });
  };

  const handleReorderTab = (activeId, overId, subCategory) => {
    setTabs(prev => {
      const categoryTabs = prev.filter(t => t.subCategory === subCategory);
      categoryTabs.sort((a, b) => (a.Section || '').localeCompare((b.Section || ''), undefined, { numeric: true }));

      const oldIndex = categoryTabs.findIndex(t => (t.id || t.ScreenName) === activeId);
      const newIndex = categoryTabs.findIndex(t => (t.id || t.ScreenName) === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const newOrder = [...categoryTabs];
      const [moved] = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, moved);

      const destCategory = categoryTabs[0].category;
      const idMapping = {};

      newOrder.forEach((t, index) => {
        const nextSequenceNumber = index + 1;
        let newSection = '';
        if (destCategory.toLowerCase() === 'footer') {
          newSection = `footer-${nextSequenceNumber}`;
        } else if (destCategory.toLowerCase() === 'body') {
          newSection = `${subCategory}-${nextSequenceNumber}`;
        } else {
          newSection = `${subCategory}-${nextSequenceNumber}`;
        }
        idMapping[t.id || t.ScreenName] = newSection;
      });

      const updatedTabs = prev.map(t => {
        if (t.subCategory === subCategory) {
          const newSec = idMapping[t.id || t.ScreenName];
          return { ...t, Section: newSec, id: newSec };
        }
        return t;
      });

      setActiveTabIds(curr => {
        const newActive = { ...curr };
        if (curr[subCategory] && idMapping[curr[subCategory]]) {
          newActive[subCategory] = idMapping[curr[subCategory]];
        }
        return newActive;
      });

      setTabContainers(curr => {
        const newContainers = { ...curr };
        const temp = {};
        newOrder.forEach(t => {
          const oldId = t.id || t.ScreenName;
          const newId = idMapping[oldId];
          if (newContainers[oldId]) {
            temp[newId] = newContainers[oldId].map(container => {
              if (container.data && typeof container.data === 'object') {
                return { ...container, data: { ...container.data, Section: newId } };
              }
              return container;
            });
          }
        });

        newOrder.forEach(t => {
          delete newContainers[t.id || t.ScreenName];
        });

        Object.assign(newContainers, temp);
        return newContainers;
      });

      return updatedTabs;
    });
  };

  const availableCategories = categories.filter(cat => groupedTabs[cat] && groupedTabs[cat].length > 0);
  const [activeCategory, setActiveCategory] = useState('Header');

  useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.includes(activeCategory)) {
      setActiveCategory(availableCategories[0]);
    }
  }, [availableCategories, activeCategory]);

  const getFinalSchema = () => {
    const finalSchema = [];

    tabs.forEach(tab => {
      if (tab.id === 'schema-tab') {
        const schemaContainers = tabContainers[tab.id] || [];
        schemaContainers.forEach(container => {
          finalSchema.push(container.data);
        });
      } else {
        const tabData = { ...tab };
        delete tabData.id;
        delete tabData.name;
        delete tabData.category;
        delete tabData.subCategory;

        const containers = tabContainers[tab.id] || [];
        tabData.SourceFields = containers.map(c => c.data);

        finalSchema.push(tabData);
      }
    });
    return finalSchema;
  };

  const executeSaveScreenMapping = async () => {
    const finalSchema = getFinalSchema();

    try {
      const res = await postRequest('api/transactions/importScreenMapping', {
        data: finalSchema,
        TenantID: currentTenantId,
        ScreenName: currentScreenName,
      });
      console.log('Save response:', res);
    } catch (err) {
      console.error('Error saving screen mapping:', err);
    }
  };

  const handleSaveScreenMapping = () => {
    setConfirmDialog({
      open: true,
      type: 'save',
      title: 'Save Screen Mapping',
      message: 'Are you sure you want to save the screen mapping?'
    });
  };

  const executeStoreScreenJson = async () => {
    try {
      const payload = {
        data: getFinalSchema(),
        Process: "Store Screen Json",
        TenantID: currentTenantId,
        ScreenName: currentScreenName,
        ProductCode: localStorage.getItem('module')
      };

      const res = await postRequest('api/transactions/storeScreenJson', payload);
      console.log('Store Screen JSON response:', res);
    } catch (err) {
      console.error('Error storing screen JSON:', err);
    }
  };

  const handleStoreScreenJson = () => {
    setConfirmDialog({
      open: true,
      type: 'store',
      title: 'Store Screen JSON',
      message: 'Are you sure you want to store the screen JSON and trigger the Lambda?'
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.type === 'save') {
      executeSaveScreenMapping();
    } else if (confirmDialog.type === 'store') {
      executeStoreScreenJson();
    }
    setConfirmDialog({ open: false, type: null, title: '', message: '' });
  };

  return (
    <Box className="schema-page" sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Filter Section */}
      <FilterSection
        onSearch={handleSearch}
        extraActions={
          hasSearched && (
            <>
              <Button
                variant="outlined"
                startIcon={<Save />}
                size="small"
                color="secondary"
                sx={{ whiteSpace: 'nowrap', py: 0.25, px: 1.5, fontSize: '0.75rem', height: 32, borderRadius: 2 }}
                onClick={handleSaveScreenMapping}
              >
                Save Screen Mapping
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                size="small"
                color="info"
                sx={{ whiteSpace: 'nowrap', py: 0.25, px: 1.5, fontSize: '0.75rem', height: 32, borderRadius: 2 }}
                onClick={handleStoreScreenJson}
              >
                Store Screen JSON
              </Button>
            </>
          )
        }
      />

      {hasSearched ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 4 }}>
          {/* Top-Level Category Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeCategory}
              onChange={(e, newVal) => setActiveCategory(newVal)}
              textColor="primary"
              indicatorColor="primary"
            >
              {availableCategories.map(cat => (
                <Tab key={cat} label={`${cat} Tabs`} value={cat} sx={{ fontWeight: 'bold' }} />
              ))}
            </Tabs>
          </Box>

          {availableCategories.includes(activeCategory) && (() => {
            const catTabs = groupedTabs[activeCategory];
            const subCategoriesMap = catTabs.reduce((acc, tab) => {
              const subCat = tab.subCategory || tab.category || 'Other';
              if (!acc[subCat]) acc[subCat] = [];
              acc[subCat].push(tab);
              return acc;
            }, {});

            Object.keys(subCategoriesMap).forEach(key => {
              subCategoriesMap[key].sort((a, b) => {
                const secA = a.Section || '';
                const secB = b.Section || '';
                return secA.localeCompare(secB, undefined, { numeric: true, sensitivity: 'base' });
              });
            });

            // Sort subCategories logically
            const sortedSubCats = Object.keys(subCategoriesMap).sort((a, b) => {
              const numA = parseInt(a);
              const numB = parseInt(b);
              if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
              return a.localeCompare(b);
            });

            return sortedSubCats.map(subCat => {
              const catActiveTabId = activeTabIds[subCat] || subCategoriesMap[subCat][0]?.id;
              const isSchema = catActiveTabId === 'schema-tab';
              const currentContainers = tabContainers[catActiveTabId] || [];
              const isAddingContainer = addingContainerCategory === subCat;

              return (
                <Box key={`section-${subCat}`} sx={{ p: 3, mb: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', textTransform: 'uppercase', fontWeight: 'bold' }}>
                    {subCat === activeCategory ? `${activeCategory} SECTION` : `${subCat.toUpperCase()} SECTION`}
                  </Typography>

                  {/* Tab Bar Strip for this Category */}
                  <TabBar
                    tabs={subCategoriesMap[subCat]}
                    activeTabId={catActiveTabId}
                    setActiveTabId={(id) => setActiveTabIds(prev => ({ ...prev, [subCat]: id }))}
                    onAddTab={(name) => handleAddTab(name, activeCategory, subCat)}
                    onRenameTab={handleRenameTab}
                    onUpdateTab={handleUpdateTab}
                    onMoveTab={handleMoveTab}
                    onReorderTab={(activeId, overId) => handleReorderTab(activeId, overId, subCat)}
                    moveOptions={activeCategory === 'Header' ? null : moveOptions}
                    extraActions={
                      !isAddingContainer && (
                        <Button
                          onClick={() => handleStartAddContainer(subCat)}
                          className="schema-add-container-btn"
                          variant="contained"
                          startIcon={<Plus />}
                          size="small"
                          sx={{ whiteSpace: 'nowrap', py: 0.25, px: 1.5, fontSize: '0.75rem', height: 32, borderRadius: 2 }}
                        >
                          {isSchema ? 'Add Container' : 'Add Field'}
                        </Button>
                      )
                    }
                  />

                  {/* Container List Workspace for this Category */}
                  <DndContext
                    id={`dnd-context-${subCat}`}
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, catActiveTabId)}
                  >
                    <Box className="schema-workspace-container" sx={{ mt: 3 }}>
                      <SortableContext
                        items={currentContainers.map(c => c.id)}
                        strategy={rectSortingStrategy}
                      >
                        <Box
                          className="schema-containers-list"
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(3, 1fr)' },
                            gap: 3,
                            alignItems: 'start'
                          }}
                        >
                          {currentContainers.map((container) => (
                            <SortableItem key={container.id} id={container.id}>
                              <ContainerCard
                                id={container.id}
                                title={container.title}
                                isCollapsed={container.isCollapsed}
                                data={container.data}
                                isSchemaTab={isSchema}
                                onToggleCollapse={(containerId) => handleToggleCollapse(catActiveTabId, containerId)}
                                onRenameContainer={(containerId, title) => handleRenameContainer(catActiveTabId, containerId, title)}
                                onUpdateContainerData={(containerId, data) => handleUpdateContainerData(catActiveTabId, containerId, data)}
                                onAddField={(containerId, name, type, value) => handleAddSubProperty(catActiveTabId, containerId, name, type, value)}
                              />
                            </SortableItem>
                          ))}
                        </Box>
                      </SortableContext>

                      {currentContainers.length === 0 && !isAddingContainer && (
                        <Box className="empty-containers-message" sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 1, border: '1px dashed grey' }}>
                          <Typography color="text.secondary">
                            {isSchema
                              ? 'No containers inside this tab. Click "+ Add Container" to create one.'
                              : 'No fields inside this tab. Click "+ Add Field" to create one.'
                            }
                          </Typography>
                        </Box>
                      )}

                      {/* Inline Add Container Form */}
                      {isAddingContainer && (
                        <Box className="inline-container-editor" sx={{ p: 2, border: '1px dashed grey', borderRadius: 1, bgcolor: 'background.default', mt: 2 }}>
                          <Typography variant="caption" className="editor-label" sx={{ mb: 1, display: 'block' }}>
                            {isSchema ? 'Container Name' : 'Field Name'}
                          </Typography>
                          <Box className="inline-container-row" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Autocomplete
                              freeSolo
                              options={[]}
                              value={newContainerTitle}
                              onInputChange={(e, newValue) => setNewContainerTitle(newValue || '')}
                              fullWidth
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  inputRef={containerInputRef}
                                  size="small"
                                  variant="outlined"
                                  onKeyDown={handleContainerKeyDown}
                                  placeholder={isSchema ? 'Enter Container Title...' : 'Enter Field Name...'}
                                  className="container-editor-input"
                                />
                              )}
                            />
                            <IconButton onClick={handleSaveContainer} className="editor-action-btn save" title="Save" color="primary">
                              <Check />
                            </IconButton>
                            <IconButton onClick={handleCancelContainer} className="editor-action-btn cancel" title="Cancel" color="error">
                              <X />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </DndContext>
                </Box>
              );
            });
          })()}
        </Box>
      ) : (
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px dashed grey',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            textAlign: 'center'
          }}
        >
          <DisplaySettings sx={{ fontSize: 64, color: 'primary.light', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Ready to configure your screen?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 450 }}>
            Please select a Tenant, Screen Name in the filters above, then click Search to load the schema workspace.
          </Typography>
        </Box>
      )}

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMsg('')} severity="error" sx={{ width: '100%', boxShadow: 3, fontWeight: 'bold' }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, title: '', message: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null, title: '', message: '' })} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScreenMapping;
